import { call } from 'redux-saga/effects'
import { HANDLED, SKIP } from './constants'

/**
 * Factory — creates an error handler that invokes a callback to signal
 * the hosting modal should switch to its inline error state.
 *
 * The returned handler is a generator function that fits the standard
 * handler-chain contract. When the chain reaches it, it calls `onError`
 * with the caught error (synchronous React state setters work fine via
 * `yield call`) and returns HANDLED so no subsequent handlers run —
 * in particular, the defaultErrorHandler never fires, preventing a
 * global modal or toast from layering on top of the in-modal error state.
 *
 * Usage:
 *   import { createModalErrorHandler } from 'sagas/errorHandlers'
 *
 *   const [modalError, setModalError] = useState(null)
 *
 *   dispatch(
 *     someApiAction({
 *       ...,
 *       errorHandlers: [createModalErrorHandler(setModalError)],
 *     })
 *   )
 *
 *   // In JSX:
 *   {modalError ? <ModalErrorContent /> : <NormalContent />}
 *
 * The callback receives the error object so callers can optionally render
 * context-specific detail, though the standard ModalErrorContent uses
 * fixed copy per the Figma design.
 *
 * @param {Function} onError - Called with (err) when the handler fires.
 *                             Typically a React useState setter.
 * @returns {GeneratorFunction} A handler generator that returns HANDLED when
 *                              onError is a function (error is handled inline).
 *                              Returns SKIP when onError is not a function, so
 *                              the default handler chain still runs and shows
 *                              the generic error modal as a fallback.
 */
export const createModalErrorHandler = (onError) => {
  // Guard: if onError is not a function, return a no-op handler that still
  // returns HANDLED so the default global modal/toast doesn't appear on top of
  // whatever the modal is currently showing. Logs a console error so the bug
  // is visible in development without crashing the saga.
  if (typeof onError !== 'function') {
    // eslint-disable-next-line no-console
    console.error(
      '[createModalErrorHandler] onError must be a function, received:',
      typeof onError
    )
    // Return SKIP (not HANDLED) so the default handler chain still runs and
    // shows the generic error modal. Returning HANDLED here would silently
    // swallow the error with no user-visible feedback — a missing or malformed
    // onError is a development-time bug and should not suppress the fallback UI.
    return function* fallbackHandler() {
      // yield* [] satisfies the require-yield rule for generator functions
      // that have no natural yield effects.
      yield* []
      return SKIP
    }
  }

  return function* modalErrorHandler(err) {
    yield call(onError, err)
    return HANDLED
  }
}
